/**
 * Utilidades para manipulação de nomes de profissionais
 */

// Lista de títulos profissionais comuns (sincronizada com backend ProfessionalTitle.cs)
const PROFESSIONAL_TITLES = [
  // Saúde
  'Dr.', 'Dra.', 'Enf.', 'Fisio.', 'Nutr.', 'Psi.', 'Odont.', 'Farm.',
  'Biom.', 'Fono.', 'T.O.', 'Méd.',
  
  // Acadêmica
  'Prof.', 'Profa.', 'Prof. Dr.', 'Profa. Dra.', 'Me.', 'Ms.', 'PhD', 'Pós-Doc.',
  
  // Jurídica
  'Adv.', 'Dr. (Jur.)', 'Dra. (Jur.)', 'Proc.',
  
  // Engenharia
  'Eng.', 'Arq.', 'Téc.',
  
  // Administrativa
  'Adm.', 'Cont.', 'Econ.',
  
  // Militar
  'Cap.', 'Maj.', 'Ten. Cel.', 'Cel.', 'Gen.', 'Alm.', 'Brig.', 'Sgt.', 'Cb.',
  
  // Religiosa
  'Pe.', 'Ir.', 'Fr.', 'Pr.', 'Bp.', 'Mons.',
  
  // Formal
  'Sr.', 'Sra.', 'Srta.'
];

/**
 * Remove títulos profissionais do nome
 * @param {string} fullName - Nome completo do profissional (pode incluir título)
 * @returns {string} Nome sem títulos
 * 
 * @example
 * removeTitle('Dr. João Silva') // 'João Silva'
 * removeTitle('Profa. Dra. Maria Santos') // 'Maria Santos'
 * removeTitle('João Silva') // 'João Silva'
 */
export function removeTitle(fullName) {
  if (!fullName || typeof fullName !== 'string') return '';
  
  let cleanName = fullName.trim();
  
  // Ordena títulos por comprimento (maior primeiro) para evitar remoção parcial
  const sortedTitles = [...PROFESSIONAL_TITLES].sort((a, b) => b.length - a.length);
  
  // Remove títulos do início do nome
  for (const title of sortedTitles) {
    if (cleanName.startsWith(title)) {
      cleanName = cleanName.substring(title.length).trim();
    }
  }
  
  return cleanName;
}

/**
 * Extrai iniciais do nome (primeira letra do primeiro nome e primeira letra do último sobrenome)
 * Remove títulos profissionais antes de extrair as iniciais
 * 
 * @param {string} fullName - Nome completo do profissional
 * @returns {string} Iniciais (2 letras em maiúsculo)
 * 
 * @example
 * getInitials('Dr. João Pedro Silva') // 'JS'
 * getInitials('Profa. Maria') // 'M' (se tiver só um nome, retorna só uma letra)
 * getInitials('Dr. Ana Paula Costa Santos') // 'AS'
 * getInitials('João') // 'J'
 */
export function getInitials(fullName) {
  if (!fullName || typeof fullName !== 'string') return '?';
  
  // Remove títulos
  const cleanName = removeTitle(fullName);
  
  // Divide em palavras e remove vazios
  const words = cleanName
    .split(/\s+/)
    .filter(word => word.length > 0);
  
  if (words.length === 0) return '?';
  if (words.length === 1) {
    // Se tem só um nome, retorna só a primeira letra
    return words[0][0].toUpperCase();
  }
  
  // Primeira letra do primeiro nome + primeira letra do último sobrenome
  const firstInitial = words[0][0].toUpperCase();
  const lastInitial = words[words.length - 1][0].toUpperCase();
  
  return firstInitial + lastInitial;
}

/**
 * Retorna o nome completo do profissional para exibição
 * Se tiver título, exibe com título. Senão, só o nome.
 * 
 * @param {object} professional - Objeto do profissional { name, title }
 * @returns {string} Nome formatado para exibição
 * 
 * @example
 * getDisplayName({ name: 'João Silva', title: 'Dr.' }) // 'Dr. João Silva'
 * getDisplayName({ name: 'Maria Santos', title: null }) // 'Maria Santos'
 * getDisplayName({ name: 'Dr. João Silva', title: null }) // 'Dr. João Silva' (mantém se já está no nome)
 */
export function getDisplayName(professional) {
  if (!professional || !professional.name) return '';
  
  const { name, title } = professional;
  
  // Se não tem título, retorna só o nome
  if (!title) return name;
  
  // Se o nome já começa com o título, não duplica
  if (name.trim().startsWith(title.trim())) {
    return name;
  }
  
  // Adiciona título antes do nome
  return `${title} ${name}`;
}

/**
 * Valida se uma string é um título profissional válido
 * @param {string} title - Título a ser validado
 * @returns {boolean} true se for um título válido
 */
export function isValidTitle(title) {
  if (!title || typeof title !== 'string') return false;
  return PROFESSIONAL_TITLES.includes(title.trim());
}

/**
 * Retorna todos os títulos disponíveis para sugestão
 * @returns {string[]} Array de títulos
 */
export function getAllTitles() {
  return [...PROFESSIONAL_TITLES];
}

/**
 * Busca títulos que correspondem ao filtro
 * @param {string} filter - Texto para filtrar títulos
 * @returns {string[]} Array de títulos filtrados
 * 
 * @example
 * searchTitles('dr') // ['Dr.', 'Dra.', 'Dr. (Jur.)', 'Dra. (Jur.)', ...]
 * searchTitles('prof') // ['Prof.', 'Profa.', 'Prof. Dr.', 'Profa. Dra.']
 */
export function searchTitles(filter) {
  if (!filter || typeof filter !== 'string') return getAllTitles();
  
  const lowerFilter = filter.toLowerCase();
  return PROFESSIONAL_TITLES.filter(title => 
    title.toLowerCase().includes(lowerFilter)
  );
}

export default {
  removeTitle,
  getInitials,
  getDisplayName,
  isValidTitle,
  getAllTitles,
  searchTitles,
  PROFESSIONAL_TITLES
};
